import React, { useEffect, useRef, useState } from 'react';
import { api } from './api';
import {
  computeAchievements, computeQuests, totalXp, levelInfo, computeStats,
  tierForOverall, formatDate,
} from './game';
import './styles.css';

const EMPTY_METRICS = { date: '', sprint: '', slalomNo: '', slalomBall: '', juggle: '', weak: '', note: '' };
const METRICS = [
  { key: 'sprint', icon: '⚡', name: 'Спринт 30м', hint: 'Старт: 6.43 сек', unit: 'сек', step: '0.01', lowerBetter: true },
  { key: 'slalomNo', icon: '🏃', name: 'Слалом без мяча', hint: 'Старт: 6.28 сек', unit: 'сек', step: '0.01', lowerBetter: true },
  { key: 'slalomBall', icon: '⚽', name: 'Слалом с мячом', hint: 'Старт: 9.60 сек', unit: 'сек', step: '0.01', lowerBetter: true },
  { key: 'juggle', icon: '🎪', name: 'Жонглёж — обе ноги', hint: 'Чередовать ноги', unit: 'раз', step: '1', lowerBetter: false },
  { key: 'weak', icon: '🦶', name: 'Слабая нога', hint: 'Только слабой', unit: 'раз', step: '1', lowerBetter: false },
];

export default function App() {
  const [weekData, setWeekData] = useState({});
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('platon');
  const [platonTab, setPlatonTab] = useState('career');
  const [trainerTab, setTrainerTab] = useState('trainer');
  const [activeWeek, setActiveWeek] = useState(1);
  const [form, setForm] = useState(EMPTY_METRICS);
  const [trainerPin, setTrainerPin] = useState('');

  const [pinShow, setPinShow] = useState(false);
  const [pinWho, setPinWho] = useState('trainer');
  const [pinBuffer, setPinBuffer] = useState('');
  const [pinError, setPinError] = useState('');

  const [toast, setToast] = useState('');
  const [confetti, setConfetti] = useState(false);
  const [levelup, setLevelup] = useState(null);
  const [questUnlock, setQuestUnlock] = useState(null);
  const [cardBounce, setCardBounce] = useState(false);

  const pendingQuestsRef = useRef([]);
  const toastTimer = useRef(null);

  useEffect(() => {
    api.getWeeks().then((r) => {
      setWeekData(r.weeks || {});
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    const d = weekData[activeWeek] || {};
    setForm({
      date: d.date || '',
      sprint: d.sprint ?? '',
      slalomNo: d.slalomNo ?? '',
      slalomBall: d.slalomBall ?? '',
      juggle: d.juggle ?? '',
      weak: d.weak ?? '',
      note: d.note || '',
    });
  }, [activeWeek, weekData]);

  if (loading) return <div className="phone" style={{ alignItems: 'center', justifyContent: 'center' }}>Загрузка…</div>;

  const quests = computeQuests(weekData);
  const captainQuest = quests[quests.length - 1];
  const achievements = computeAchievements(weekData, captainQuest.done);
  const xp = totalXp(weekData);
  const lvl = levelInfo(xp);
  const stats = computeStats(weekData);
  const tier = tierForOverall(stats.overall);
  const tierLabel = tier === 'gold' ? 'Золото' : tier === 'silver' ? 'Серебро' : 'Бронза';

  function showToast(msg) {
    setToast(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(''), 2800);
  }

  function fireConfetti() {
    setConfetti(true);
    setTimeout(() => setConfetti(false), 3000);
  }

  function bounceCard() {
    setCardBounce(true);
    setTimeout(() => setCardBounce(false), 350);
  }

  function openPin() { setPinBuffer(''); setPinError(''); setPinWho('trainer'); setPinShow(true); }
  function closePin() { setPinShow(false); setPinBuffer(''); setPinError(''); }

  function pinPress(d) {
    if (pinWho === 'platon' || pinBuffer.length >= 4) return;
    const next = pinBuffer + d;
    setPinBuffer(next);
    if (next.length === 4) setTimeout(() => checkPin(next), 120);
  }
  function pinDel() { setPinBuffer((b) => b.slice(0, -1)); }

  async function checkPin(buffer) {
    if (pinWho === 'platon') { closePin(); showToast('⚽ Режим Платона'); return; }
    try {
      const r = await api.verifyPin(buffer);
      if (r.ok) {
        setTrainerPin(buffer);
        closePin();
        setMode('trainer');
        showToast('✅ Добро пожаловать, тренер!');
      } else {
        wrongPin();
      }
    } catch {
      wrongPin();
    }
  }
  function wrongPin() {
    setPinError('Неверный код. Попробуй ещё раз.');
    setTimeout(() => { setPinBuffer(''); }, 700);
  }

  function exitTrainer() { setMode('platon'); }

  function selectWho(who) {
    if (who === 'platon') {
      closePin();
      showToast('⚽ Режим Платона');
      return;
    }
    setPinWho(who);
    setPinBuffer('');
    setPinError('');
  }

  async function saveWeek() {
    const prevQuestsDone = new Set(quests.filter((q) => q.done).map((q) => q.id));
    const prevAchUnlocked = new Set(achievements.filter((a) => a.unlocked).map((a) => a.id));
    const prevLevel = lvl.level;

    const payload = {
      date: form.date || '',
      sprint: form.sprint === '' ? null : parseFloat(form.sprint),
      slalomNo: form.slalomNo === '' ? null : parseFloat(form.slalomNo),
      slalomBall: form.slalomBall === '' ? null : parseFloat(form.slalomBall),
      juggle: form.juggle === '' ? null : parseInt(form.juggle, 10),
      weak: form.weak === '' ? null : parseInt(form.weak, 10),
      note: form.note || '',
    };

    try {
      const r = await api.saveWeek(activeWeek, payload, trainerPin);
      const newWeekData = { ...weekData, [activeWeek]: r.week };
      setWeekData(newWeekData);

      const newQuests = computeQuests(newWeekData);
      const newAch = computeAchievements(newWeekData, newQuests[newQuests.length - 1].done);
      const newXp = totalXp(newWeekData);
      const newLevel = levelInfo(newXp).level;

      showToast(`✅ Неделя ${activeWeek} сохранена!`);

      const newlyAch = newAch.filter((a) => a.unlocked && !prevAchUnlocked.has(a.id));
      newlyAch.forEach((a) => showToast(`${a.emoji} ${a.name}! +${a.xp} XP`));

      const newlyQuests = newQuests.filter((q) => q.done && !prevQuestsDone.has(q.id));
      if (newlyQuests.length) {
        pendingQuestsRef.current = newlyQuests.slice(1);
        setQuestUnlock(newlyQuests[0]);
        fireConfetti();
      }

      if (newLevel > prevLevel) {
        const delay = newlyQuests.length ? 2000 : 300;
        setTimeout(() => setLevelup(levelInfo(newXp)), delay);
      }
    } catch (e) {
      showToast('❌ Ошибка сохранения: ' + e.message);
    }
  }

  function closeQuestUnlock() {
    setQuestUnlock(null);
    if (pendingQuestsRef.current.length) {
      const next = pendingQuestsRef.current.shift();
      setTimeout(() => { setQuestUnlock(next); fireConfetti(); }, 300);
    }
  }

  function trend(metric) {
    const cur = weekData[activeWeek];
    const prev = weekData[activeWeek - 1];
    const curVal = cur ? cur[metric.key] : null;
    const prevVal = prev ? prev[metric.key] : null;
    if (curVal == null || prevVal == null) return '—';
    if (curVal === prevVal) return '➡️';
    const better = metric.lowerBetter ? curVal < prevVal : curVal > prevVal;
    return better ? '📈' : '📉';
  }

  async function generatePdf() {
    const { jsPDF } = await import('jspdf');
    await import('jspdf-autotable');
    const doc = new jsPDF();

    doc.setFontSize(16);
    doc.text('ПЛАТОН — Прогресс за лето 2026', 14, 16);
    doc.setFontSize(10);
    doc.text(`Дата отчёта: ${new Date().toLocaleDateString('ru-RU')}`, 14, 23);
    doc.text(`FIFA-карточка: ${stats.overall} OVERALL · ${tierLabel}`, 14, 30);
    doc.text(`Уровень: ${lvl.title} (Всего XP: ${xp})`, 14, 36);

    const weeks = Object.keys(weekData).map(Number).sort((a, b) => a - b).filter((w) => weekData[w]);
    const rows = weeks.map((w) => {
      const d = weekData[w];
      return [w, formatDate(d.date), d.sprint ?? '—', d.slalomNo ?? '—', d.slalomBall ?? '—', d.juggle ?? '—', d.weak ?? '—', d.note || ''];
    });

    doc.autoTable({
      startY: 42,
      head: [['Нед.', 'Дата', 'Спринт', 'Сл.без', 'Сл.с мячом', 'Жонглёж', 'Сл.нога', 'Заметка']],
      body: rows,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [26, 61, 43] },
    });

    let y = (doc.lastAutoTable?.finalY || 42) + 10;
    const doneQ = quests.filter((q) => q.done);
    const doneA = achievements.filter((a) => a.unlocked);
    doc.setFontSize(10);
    if (doneQ.length) { doc.text(`Выполненные квесты: ${doneQ.map((q) => q.name).join(', ')}`, 14, y); y += 6; }
    if (doneA.length) { doc.text(`Достижения: ${doneA.map((a) => a.name).join(', ')}`, 14, y); }

    const filename = `platon-progress-${new Date().toISOString().slice(0, 10)}.pdf`;
    const blob = doc.output('blob');
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
  }

  return (
    <div className="phone">
      {mode === 'platon' && (
        <>
          <div className="platon-header">
            <div className="platon-title">⚽ Платон — <span>Тренировки</span></div>
            <div className="mode-badge" onClick={openPin}>
              <div className="dot" />
              Режим Платона · переключить
            </div>
            <div className="platon-tabs">
              <button className={`ptab ${platonTab === 'quests' ? 'active' : 'inactive'}`} onClick={() => setPlatonTab('quests')}>🎯 Квесты</button>
              <button className={`ptab ${platonTab === 'career' ? 'active' : 'inactive'}`} onClick={() => setPlatonTab('career')}>🏆 Карьера</button>
            </div>
          </div>

          <div className="platon-content">
            {platonTab === 'career' && (
              <div className="platon-scroll">
                <div className="fifa-card-wrap">
                  <div className={`fifa-card ${tier} pulse ${cardBounce ? 'bounce' : ''}`} onClick={bounceCard}>
                    <div className="card-overall"><div className="number">{stats.overall}</div><div className="tier">{tierLabel}</div></div>
                    <div className="card-pos">ST</div>
                    <div className="card-ball">⚽</div>
                    <div className="card-name">ПЛАТОН</div>
                    <div className="card-stats">
                      <div className="card-stat"><span className="label">Скорость</span><span className="value">{stats.pac}</span></div>
                      <div className="card-stat"><span className="label">Дриблинг</span><span className="value">{stats.dri}</span></div>
                      <div className="card-stat"><span className="label">Удар</span><span className="value">{stats.sho}</span></div>
                      <div className="card-stat"><span className="label">Защита</span><span className="value">{stats.def}</span></div>
                      <div className="card-stat"><span className="label">Пас</span><span className="value">{stats.pas}</span></div>
                      <div className="card-stat"><span className="label">Физика</span><span className="value">{stats.phy}</span></div>
                    </div>
                  </div>
                </div>
                <div className="card-hint">Характеристики растут от тренировок · XP — за выполнение заданий</div>

                <div className="xp-section">
                  <div className="xp-header">
                    <div className="xp-level">⚡ Уровень {lvl.level}</div>
                    <div className="xp-count">{xp - lvl.xp} / {lvl.nextXp - lvl.xp} XP</div>
                  </div>
                  <div className="xp-title">{lvl.title}</div>
                  <div className="xp-bar-bg"><div className="xp-bar-fill" style={{ width: `${lvl.pct}%` }} /></div>
                  <div className="xp-total">Всего XP: {xp}</div>
                </div>

                <div className="section-title-career">🏅 Достижения</div>
                <div className="achievements-grid">
                  {achievements.map((a) => (
                    <div
                      key={a.id}
                      className={`ach-card ${a.unlocked ? 'unlocked' : 'locked'}`}
                      onClick={() => { if (!a.unlocked) showToast('🔒 Ещё не разблокировано'); }}
                    >
                      {a.unlocked && <div className="ach-badge">✅</div>}
                      <span className="ach-emoji">{a.emoji}</span>
                      <div className="ach-name">{a.name}</div>
                      <div className="ach-desc">{a.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {platonTab === 'quests' && (
              <div className="platon-scroll">
                <div className="quest-header-card">
                  <div className="quest-header-icon">☀️</div>
                  <div className="quest-header-text">
                    <div className="title">Квесты лета 2026</div>
                    <div className="sub">Прогресс обновляется автоматически</div>
                  </div>
                  <div className="quest-total-xp"><div className="num">{xp}</div><div className="lbl">XP заработано</div></div>
                </div>

                {quests.map((q) => {
                  if (q.done) {
                    return (
                      <div key={q.id} className="quest-card done-quest">
                        <div className="quest-top">
                          <div className="quest-icon">{q.icon}</div>
                          <div className="quest-info">
                            <div className="quest-name">{q.name} ✅</div>
                            <div className="quest-desc">{q.desc}</div>
                          </div>
                          <div className="quest-xp-badge">+{q.xp} XP ✓</div>
                        </div>
                      </div>
                    );
                  }
                  if (q.locked) {
                    return (
                      <div key={q.id} className="quest-card locked-quest">
                        <div className="quest-top">
                          <div className="quest-icon">🔒</div>
                          <div className="quest-info">
                            <div className="quest-name">{q.name}</div>
                            <div className="quest-desc">{q.desc}</div>
                          </div>
                          <div className="quest-xp-badge">+{q.xp} XP</div>
                        </div>
                        <div className="quest-lock-msg">Выполни все квесты выше чтобы открыть</div>
                      </div>
                    );
                  }
                  return (
                    <div key={q.id} className="quest-card active-quest">
                      <div className="quest-top">
                        <div className="quest-icon">{q.icon}</div>
                        <div className="quest-info">
                          <div className="quest-name">{q.name}</div>
                          <div className="quest-desc">{q.desc}</div>
                        </div>
                        <div className="quest-xp-badge">+{q.xp} XP</div>
                      </div>
                      <div className="quest-progress-labels">
                        <span>{q.progress.isPct ? q.progress.label : `${q.progress.val} / ${q.progress.total} ${q.progress.label}`}</span>
                        <span>{q.progress.pct}%</span>
                      </div>
                      <div className="quest-progress-bar"><div className="quest-progress-fill" style={{ width: `${q.progress.pct}%` }} /></div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {mode === 'trainer' && (
        <div className="trainer-wrap active">
          <div className="trainer-header">
            <div className="trainer-title">📋 Тренер — <span>Данные</span></div>
            <div className="exit-badge" onClick={exitTrainer}>← Вернуться к Платону</div>
            <div className="trainer-tabs">
              <button className={`ttab ${trainerTab === 'trainer' ? 'active' : ''}`} onClick={() => setTrainerTab('trainer')}>Тренер</button>
              <button className={`ttab ${trainerTab === 'export' ? 'active' : ''}`} onClick={() => setTrainerTab('export')}>📄 Экспорт PDF</button>
            </div>
          </div>

          {trainerTab === 'export' && (
            <div className="trainer-body">
              <div className="section-label">📄 Экспорт отчёта</div>
              <div className="form-card" style={{ marginBottom: 14 }}>
                <div className="form-card-title">Отчёт о прогрессе — ПЛАТОН</div>
                <div style={{ padding: 16, fontSize: 12, color: '#1a2a20', lineHeight: 1.8 }}>
                  <b>{stats.overall} OVERALL · {tierLabel}</b><br />
                  Уровень: {lvl.title} (Всего XP: {xp})<br />
                  Недель заполнено: {Object.values(weekData).filter(Boolean).length}/8
                </div>
              </div>
              <button className="save-btn" onClick={generatePdf}>📋 Скачать PDF-отчёт</button>
              <div className="export-hint">Формат: PDF-файл со сводкой и таблицей недель</div>
            </div>
          )}

          {trainerTab === 'trainer' && (
            <div className="trainer-body">
              <div className="section-label">📅 Выбери неделю</div>
              <div className="week-row">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((w) => (
                  <div
                    key={w}
                    className={`wk ${w === activeWeek ? 'cur' : weekData[w] ? 'done' : 'empty'}`}
                    onClick={() => setActiveWeek(w)}
                  >
                    {w}
                  </div>
                ))}
              </div>

              <div className="form-card">
                <div className="form-card-title">Неделя {activeWeek} — данные тренировки</div>
                <div className="mrow">
                  <div className="micon">📅</div>
                  <div className="minfo"><div className="mname">Дата тренировки</div><div className="mhint">День недели подтянется автоматически</div></div>
                  <div className="metric-input-wrap">
                    <input className="minput" type="date" style={{ width: 130, fontSize: 12, fontWeight: 700 }}
                      value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} />
                  </div>
                </div>
                {METRICS.map((m) => (
                  <div className="mrow" key={m.key}>
                    <div className="micon">{m.icon}</div>
                    <div className="minfo"><div className="mname">{m.name}</div><div className="mhint">{m.hint}</div></div>
                    <div className="metric-input-wrap">
                      <input
                        className="minput" type="number" step={m.step}
                        value={form[m.key]}
                        onChange={(e) => setForm((f) => ({ ...f, [m.key]: e.target.value }))}
                      />
                      <div className="munit">{m.unit}</div>
                      <div className="metric-trend">{trend(m)}</div>
                    </div>
                  </div>
                ))}
                <div className="note-row">
                  <div className="note-label">Заметка тренера</div>
                  <textarea
                    className="note-input" placeholder="Что было хорошо / над чем работать…"
                    value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                  />
                </div>
              </div>
              <button className="save-btn" onClick={saveWeek}>💾 Сохранить неделю</button>
              <div className="export-hint">Экспорт PDF — во вкладке выше</div>
            </div>
          )}
        </div>
      )}

      {pinShow && (
        <div className="pin-overlay show" onClick={(e) => { if (e.target === e.currentTarget) closePin(); }}>
          <div className="pin-sheet">
            <div className="pin-handle" />
            <div className="pin-who">
              <div className={`pin-who-btn ${pinWho === 'trainer' ? 'selected' : ''}`} onClick={() => selectWho('trainer')}>
                <span className="pin-who-icon">📋</span>
                <div className="pin-who-label">Тренер</div>
              </div>
              <div className={`pin-who-btn ${pinWho === 'platon' ? 'selected' : ''}`} onClick={() => selectWho('platon')}>
                <span className="pin-who-icon">⚽</span>
                <div className="pin-who-label">Платон</div>
              </div>
            </div>
            <div className="pin-title">{pinWho === 'trainer' ? 'Код тренера' : 'Режим Платона'}</div>
            <div className="pin-sub">{pinWho === 'trainer' ? 'Введи 4-значный пин-код' : 'Пин не нужен — просто войди!'}</div>
            <div className="pin-dots">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className={`pin-dot ${i < pinBuffer.length ? 'filled' : ''} ${pinError ? 'error' : ''}`} />
              ))}
            </div>
            <div className="pin-grid">
              {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
                <button key={d} className="pin-btn" onClick={() => pinPress(d)}>{d}</button>
              ))}
              <button className="pin-btn empty" />
              <button className="pin-btn" onClick={() => pinPress('0')}>0</button>
              <button className="pin-btn del" onClick={pinDel}>⌫</button>
            </div>
            <div className="pin-error-msg">{pinError}</div>
          </div>
        </div>
      )}

      {toast && <div className="toast show">{toast}</div>}

      {confetti && (
        <div className="confetti-wrap">
          {Array.from({ length: 60 }).map((_, i) => {
            const colors = ['#d01f3c', '#ffd700', '#006600', '#4daa72', '#fff'];
            return (
              <div
                key={i}
                className="confetti-piece"
                style={{
                  left: Math.random() * 390,
                  background: colors[Math.floor(Math.random() * colors.length)],
                  animationDelay: `${Math.random() * 0.8}s`,
                  animationDuration: `${1.5 + Math.random()}s`,
                }}
              />
            );
          })}
        </div>
      )}

      {levelup && (
        <div className="levelup show">
          <div className="levelup-emoji">🎉</div>
          <div className="levelup-sub">Новый уровень!</div>
          <div className="levelup-level">{levelup.level}</div>
          <div className="levelup-name">{levelup.title}</div>
          <button className="levelup-close" onClick={() => setLevelup(null)}>Огонь! 🔥</button>
        </div>
      )}

      {questUnlock && (
        <div className="quest-unlock show">
          <div className="qu-icon">{questUnlock.icon}</div>
          <div className="qu-title">Квест выполнен!</div>
          <div className="qu-name">{questUnlock.name}</div>
          <div className="qu-xp">+{questUnlock.xp} XP</div>
          <button className="qu-close" onClick={closeQuestUnlock}>Получить награду! 🏆</button>
        </div>
      )}
    </div>
  );
}
